import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  FileText, Clock, Users, Trophy, Play, CheckCircle,
  Loader2, BookOpen, Calendar, Target, BarChart3,
  Download, Plus, Lock, Crown
} from 'lucide-react';

import { getApiBaseUrl } from '../services/api';
const API_URL = getApiBaseUrl();

const TEST_TYPE_CONFIG = {
  chapter_quiz: {
    name: "Chapter Quiz",
    questions: 10,
    duration: 10,
    color: "emerald",
    icon: "📝"
  },
  unit_test: {
    name: "Unit Test",
    questions: 50,
    duration: 60,
    color: "blue",
    icon: "📚"
  },
  monthly_test: {
    name: "Monthly Test",
    questions: 100,
    duration: 120,
    color: "purple",
    icon: "🎯"
  }
};

const TestsHomePage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [tests, setTests] = useState([]);
  const [myHistory, setMyHistory] = useState({ attempts: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [creatingKey, setCreatingKey] = useState(null); // Track which button is loading: "type_subject"
  const [activeTab, setActiveTab] = useState('available');
  
  // Freemium: Check if user is PRO
  const isPro = user?.role === "PRO";

  useEffect(() => {
    if (!user || !token) {
      navigate('/auth');
      return;
    }
    fetchTests();
    fetchMyHistory();
  }, [user, token, navigate]);

  const fetchTests = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/structured-tests/list?status=ready`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setTests(data.tests || []);
    } catch (error) {
      console.error('Error fetching tests:', error);
      toast.error('Failed to load tests');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/structured-tests/my-history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setMyHistory(data);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const createQuickTest = async (testType, subject) => {
    console.log('Creating test:', testType, subject);
    const buttonKey = `${testType}_${subject}`;
    setCreatingKey(buttonKey);
    try {
      console.log('Calling API...');
      const res = await fetch(
        `${API_URL}/api/structured-tests/quick-create/${testType}?subject=${subject}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (!res.ok) throw new Error('Failed to create');
      const data = await res.json();
      console.log('Test created:', data);
      
      toast.success('Test created! Generating questions...');
      
      // Poll for status
      let attempts = 0;
      const maxAttempts = 30;
      
      const checkStatus = setInterval(async () => {
        attempts++;
        const statusRes = await fetch(
          `${API_URL}/api/structured-tests/${data.test_id}/status`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const statusData = await statusRes.json();
        
        if (statusData.status === 'ready') {
          clearInterval(checkStatus);
          toast.success('Test ready! Redirecting...');
          navigate(`/tests/${data.test_id}`);
        } else if (attempts >= maxAttempts || statusData.status === 'error') {
          clearInterval(checkStatus);
          toast.error('Test generation failed');
          setCreatingKey(null);
        }
      }, 2000);
      
    } catch (error) {
      console.error('Error creating test:', error);
      toast.error('Failed to create test');
      setCreatingKey(null);
    }
  };

  const startTest = (testId) => {
    navigate(`/tests/${testId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-slate-500">Loading tests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            📝 Structured Tests
          </h1>
          <p className="text-slate-500">
            Formal assessments with timing, rankings, and detailed analytics
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Target className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-slate-500 text-sm">Tests Taken</p>
                <p className="text-2xl font-bold text-slate-900">{myHistory.stats.tests_taken || 0}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-slate-500 text-sm">Average Score</p>
                <p className="text-2xl font-bold text-slate-900">{myHistory.stats.average_score || 0}%</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Trophy className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-slate-500 text-sm">Best Score</p>
                <p className="text-2xl font-bold text-slate-900">{myHistory.stats.best_score || 0}%</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-slate-500 text-sm">Total Attempts</p>
                <p className="text-2xl font-bold text-slate-900">{myHistory.stats.total_attempts || 0}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Create Section */}
        <Card className="bg-gradient-to-r from-emerald-50 to-blue-50 border-emerald-200 mb-8">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <Plus className="h-5 w-5 text-emerald-600" />
              Quick Create Practice Test
              {!isPro && (
                <Badge variant="outline" className="ml-2 text-amber-600 border-amber-300">
                  <Lock className="h-3 w-3 mr-1" />
                  Limited
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(TEST_TYPE_CONFIG).map(([type, config]) => {
                const isProOnly = type === 'unit_test' || type === 'monthly_test';
                const isLocked = isProOnly && !isPro;
                
                return (
                  <div 
                    key={type}
                    className={`p-4 rounded-lg border transition-all ${
                      isLocked 
                        ? 'bg-slate-50 border-slate-200 opacity-80' 
                        : 'bg-white border-slate-200 hover:border-emerald-300 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-2xl">{config.icon}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className={`font-semibold ${isLocked ? 'text-slate-500' : 'text-slate-900'}`}>
                            {config.name}
                          </h3>
                          {isProOnly && (
                            <Badge className={`text-xs ${isPro ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                              <Crown className="h-3 w-3 mr-1" />
                              PRO
                            </Badge>
                          )}
                          {!isProOnly && !isPro && (
                            <Badge variant="outline" className="text-xs border-emerald-300 text-emerald-600">FREE</Badge>
                          )}
                        </div>
                        <p className={`text-sm ${isLocked ? 'text-slate-400' : 'text-slate-500'}`}>
                          {config.questions} questions • {config.duration} min
                        </p>
                      </div>
                    </div>
                    
                    {isLocked ? (
                      <div className="text-center py-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs border-amber-300 text-amber-600 hover:bg-amber-50"
                          onClick={() => navigate('/upgrade')}
                        >
                          <Lock className="h-3 w-3 mr-1" />
                          Upgrade to Unlock
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2 flex-wrap">
                        {['history', 'geography', 'polity', 'economics'].map(subject => {
                          const buttonKey = `${type}_${subject}`;
                          const isLoading = creatingKey === buttonKey;
                          return (
                            <Button
                              key={subject}
                              size="sm"
                              variant="outline"
                              className="text-xs capitalize border-slate-300 text-slate-600 hover:bg-emerald-50 hover:border-emerald-500 hover:text-emerald-700 px-3 py-2"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('Button clicked:', type, subject);
                                createQuickTest(type, subject);
                              }}
                              disabled={creatingKey !== null}
                            >
                              {isLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                              {subject}
                            </Button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-slate-200 shadow-sm mb-6">
            <TabsTrigger value="available" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
              <FileText className="h-4 w-4 mr-2" />
              Available Tests ({tests.length})
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
              <Clock className="h-4 w-4 mr-2" />
              My History ({myHistory.attempts.length})
            </TabsTrigger>
          </TabsList>

          {/* Available Tests */}
          <TabsContent value="available">
            {tests.length === 0 ? (
              <Card className="bg-white border-slate-200 shadow-sm">
                <CardContent className="p-12 text-center">
                  <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl text-slate-900 mb-2">No Tests Available</h3>
                  <p className="text-slate-500 mb-4">Create a practice test using the quick create section above!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {tests.map(test => {
                  const config = TEST_TYPE_CONFIG[test.test_type] || {};
                  return (
                    <Card 
                      key={test.test_id} 
                      className="bg-white border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all"
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <span className="text-3xl">{config.icon || '📝'}</span>
                            <div>
                              <h3 className="text-slate-900 font-semibold text-lg">{test.title}</h3>
                              <div className="flex items-center gap-3 mt-1">
                                <Badge variant="outline" className="text-xs text-slate-500 border-slate-300">
                                  {config.name || test.test_type}
                                </Badge>
                                <span className="text-slate-400 text-sm flex items-center gap-1">
                                  <FileText className="h-3 w-3" />
                                  {test.question_count} questions
                                </span>
                                <span className="text-slate-500 text-sm flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {test.duration_minutes} min
                                </span>
                                <span className="text-slate-500 text-sm flex items-center gap-1">
                                  <Users className="h-3 w-3" />
                                  {test.attempt_count} attempts
                                </span>
                              </div>
                            </div>
                          </div>
                          <Button 
                            onClick={() => startTest(test.test_id)}
                            className="bg-emerald-500 hover:bg-emerald-600"
                          >
                            <Play className="h-4 w-4 mr-2" />
                            Start Test
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* History */}
          <TabsContent value="history">
            {myHistory.attempts.length === 0 ? (
              <Card className="bg-white border-slate-200 shadow-sm">
                <CardContent className="p-12 text-center">
                  <Clock className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl text-slate-900 mb-2">No Test History</h3>
                  <p className="text-slate-500">Take your first test to see your results here!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {myHistory.attempts.map((attempt, idx) => (
                  <Card 
                    key={idx} 
                    className="bg-white border-slate-200 shadow-sm"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-slate-900 font-semibold">{attempt.test_title || 'Test'}</h3>
                          <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                            <span>{new Date(attempt.submitted_at).toLocaleDateString()}</span>
                            <span>•</span>
                            <span>{Math.floor((attempt.time_taken_seconds || 0) / 60)} min</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-2xl font-bold ${
                            attempt.percentage >= 80 ? 'text-emerald-600' :
                            attempt.percentage >= 60 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {attempt.percentage}%
                          </div>
                          <div className="text-slate-500 text-sm">
                            {attempt.score}/{attempt.total}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default TestsHomePage;
